import React, { useState, useEffect } from "react";
import {
  DatatableWrapper,
  TableBody,
  TableHeader,
  Pagination,
} from "react-bs-datatable";
import { Table, Button, Image, Form } from "react-bootstrap";
import { TablePagination, TableSearchField } from "CommonComponent";
import { useDispatch, useSelector } from "react-redux";
import { gypsyballusersListFetch } from "actions";
import UpdateUserModal from "../UpdateUserModal";
import AccessRights from "utils/AccessRights";

const headers = [
  { prop: "name", title: "Name", isSortable: true },
  { prop: "email", title: "Email", isSortable: true },
  { prop: "roles", title: "Roles", isSortable: true },
  { prop: "action", title: "Action", isSortable: false },
];

const UserList = ({ ...rest }) => {
  const dispatch = useDispatch();

  const usersRoleState = useSelector((state) => state.usersRoleState);
  const usersListFetch = useSelector(
    (state) => state.usersRoleState.usersListFetch
  );
  const themeState = useSelector((state) => state.themeState);
  const session = useSelector((state) => state.session);

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [filterValue, setFilterValue] = useState("");
  const [selectedUser, setSelectedUser] = useState({
    user_id: "",
    userName: "",
    userEmail: "",
  });
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [currentPage, setCurrentPage] = useState(1); // Defined currentPage state

  useEffect(() => {
    const object_viewed_id = session?.current_page_permissions?.object_id || "";

    dispatch(gypsyballusersListFetch(object_viewed_id));
  }, [dispatch]);

  useEffect(() => {
    if (usersRoleState.usersListFetch) {
      setData(usersRoleState.usersListFetch);
      setFilteredData(usersRoleState.usersListFetch);
    }
  }, [usersRoleState.usersListFetch]);

  useEffect(() => {
    const filtered = data.filter((row) => {
      return Object.values(row).some(
        (value) =>
          value != null &&
          value.toString().toLowerCase().includes(filterValue.toLowerCase())
      );
    });
    setFilteredData(filtered);
  }, [filterValue, data]);

  const handleFilterChange = (e) => {
    setFilterValue(e.target.value);
  };

  const updateRecord = (id, name, email) => {
    setSelectedUser({ user_id: id, userName: name, userEmail: email });
    setModalShow(true);
  };

  const hideWorkspaceConfigModal = () => {
    setModalShow(false);
    setSelectedUser({ user_id: "", userName: "", userEmail: "" });
  };

  // Pagination Logic
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  const changePage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page); // Fixed: Use setCurrentPage to update currentPage state
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage); // Fixed: Use setCurrentPage to update currentPage state
    }
  };

  return (
    <>
      <div className="main-content-div page-card">
        <DatatableWrapper
          body={currentData}
          headers={headers}
          paginationOptions={{
            rowsPerPage: rowsPerPage,
            rowsPerPageOption: [5, 10, 15, 20],
            currentPage: currentPage,
            totalRows: filteredData.length,
            onPageChange: (page) => setCurrentPage(page), // Use setCurrentPage
            onRowsPerPageChange: (rowsPerPage) => setRowsPerPage(rowsPerPage),
          }}
        >
          <div>
            <TableSearchField
              value={filterValue}
              onChange={handleFilterChange}
              className="mb-3"
            />
          </div>

          <Table>
            <TableHeader />
            <TableBody>
              {currentData.length === 0 ? (
                <tr>
                  <td colSpan={4}>
                    <div className="empty-state py-4" role="status">
                      <img src="/assets/images/emptyStateTable.png" alt="" className="empty-state__image" aria-hidden="true" />
                      <p className="empty-state__title">No Records Found</p>
                      <p className="empty-state__message">There is nothing to display yet. Try adjusting your search.</p>
                    </div>
                  </td>
                </tr>
              ) : (
              currentData.map((row) => (
                <tr key={row.id}>
                  <td className="td-pad ps-3">{row.name}</td>
                  <td className="td-pad">{row.email}</td>
                  <td className="td-pad">
                    {row.roles && row.roles.length > 0
                      ? row.roles.map((role, index) => (
                          <span key={index}>
                            {role.name}
                            {index < row.roles.length - 1 && ", "}
                          </span>
                        ))
                      : ""}
                  </td>
                  <td>
                    {AccessRights(
                      session.current_page_permissions,
                      "edit",
                      row.created_by
                    ) && row.is_deleted === "n" ? (
                      <Button
                        variant="none"
                        className="border-0"
                        onClick={() =>
                          updateRecord(row.id, row.name, row.email)
                        }
                      >
                        <Image src="/assets/images/access.svg" />
                      </Button>
                    ) : (
                      ""
                    )}
                  </td>
                </tr>
              )))}
            </TableBody>
          </Table>

          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={rowsPerPage}
            pageSizeOptions={[5, 10, 20, 50, 100]}
            onPageChange={handlePageChange}
            onPageSizeChange={(size) => {
              setRowsPerPage(size);
              setCurrentPage(1);
            }}
          />
        </DatatableWrapper>
      </div>

      <UpdateUserModal
        show={modalShow}
        onHide={hideWorkspaceConfigModal}
        user_id={selectedUser.user_id}
        userName={selectedUser.userName}
        userEmail={selectedUser.userEmail}
      />
    </>
  );
};

export default UserList;
